import { useState, useEffect } from 'react';
import { collection, query, getDocs, orderBy } from 'firebase/firestore';
import { db } from '@municipality/firebase';
import { Timestamp } from 'firebase/firestore';

// Committee types (matching municipality app structure)
export interface AuditInfo {
  name: string;
  surname: string;
}

export interface CommitteeMemberWithRole {
  memberId: string;
  memberType: 'mayor' | 'deputy-mayor' | 'councilor' | 'committee-member';
  roleType: 'president' | 'vice-president' | 'secretary' | 'member' | 'other';
  role?: Record<string, string>;
  customRoleText?: Record<string, string>;
}

export interface MunicipalCommittee {
  id: string;
  name: string;
  translations?: Record<string, { name?: string }>;
  assignedCouncilors: string[];
  assignedMembers?: CommitteeMemberWithRole[];
  assignedMembersOrder?: string[];
  municipalityId: string;
  createdAt: Date;
  updatedAt: Date;
  createdBy: AuditInfo;
  updatedBy?: AuditInfo;
}

interface CommitteeListResponse {
  items: MunicipalCommittee[];
  total: number;
}

// Helper to convert Firestore Timestamp to Date
const toDate = (value: unknown): Date => {
  if (!value) return new Date();
  if (value instanceof Date) return value;
  if (value instanceof Timestamp) return value.toDate();
  if (typeof value === 'string') return new Date(value);
  if (typeof value === 'number') return new Date(value);
  if (typeof value === 'object' && value !== null && 'toDate' in value && typeof (value as { toDate: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }
  return new Date();
};

// Convert Firestore data to MunicipalCommittee
const convertFirestoreData = (data: unknown, id: string, municipalityId: string): MunicipalCommittee => {
  const docData = data as Record<string, unknown>;
  
  const createdByData = docData.createdBy as Record<string, unknown> | undefined;
  const createdBy: AuditInfo = createdByData ? {
    name: (createdByData.name as string) || '',
    surname: (createdByData.surname as string) || '',
  } : {
    name: '',
    surname: '',
  };

  const updatedByData = docData.updatedBy as Record<string, unknown> | undefined;
  const updatedBy: AuditInfo | undefined = updatedByData ? {
    name: (updatedByData.name as string) || '',
    surname: (updatedByData.surname as string) || '',
  } : undefined;

  return {
    id,
    name: (docData.name as string) || '',
    translations: docData.translations as Record<string, { name?: string }> | undefined,
    assignedCouncilors: (docData.assignedCouncilors as string[]) || [],
    assignedMembers: docData.assignedMembers as CommitteeMemberWithRole[] | undefined,
    assignedMembersOrder: docData.assignedMembersOrder as string[] | undefined,
    municipalityId,
    createdAt: toDate(docData.createdAt),
    updatedAt: toDate(docData.updatedAt),
    createdBy,
    updatedBy,
  };
};

export const useMunicipalityCommittees = (municipalityId: string | null) => {
  const [data, setData] = useState<CommitteeListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!municipalityId) {
      setLoading(false);
      setData(null);
      return;
    }

    const fetchCommittees = async () => {
      try {
        setLoading(true);
        setError(null);

        const committeesCollection = collection(db, 'municipalities', municipalityId, 'committees');
        const snapshot = await getDocs(query(committeesCollection, orderBy('createdAt', 'desc')));

        const items: MunicipalCommittee[] = [];
        snapshot.forEach((doc) => {
          const committee = convertFirestoreData(doc.data(), doc.id, municipalityId);
          items.push(committee);
        });

        setData({
          items,
          total: items.length,
        });
      } catch (err) {
        console.error('Error fetching municipality committees:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch municipality committees');
      } finally {
        setLoading(false);
      }
    };

    fetchCommittees();
  }, [municipalityId]);

  return { data, loading, error };
};
